import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, ApiError } from '@l2u/ui/lib/api.ts';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { CompanyStatusBadge } from '@l2u/ui/components/StatusBadge.tsx';
import { Avatar } from '@l2u/ui/components/Avatar.tsx';
import { MfaBadge } from '@l2u/ui/components/MfaBadge.tsx';
import { useMfaStatuses } from '../lib/useMfaStatuses.ts';
import { StatusFilterTabs } from '../components/StatusFilterTabs.tsx';
import { NavIcon, type IconName } from '@l2u/ui/components/NavIcon.tsx';
import { ConfirmModal } from '../components/ConfirmModal.tsx';
import { Modal } from '@l2u/ui/components/Modal.tsx';
import { Field, Select, TextInput } from '@l2u/ui/components/Field.tsx';
import { MultiSelect } from '../components/MultiSelect.tsx';
import { SearchableMultiSelect } from '../components/SearchableMultiSelect.tsx';
import { StringListEditor } from '../components/StringListEditor.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { usePortalSettings } from '@l2u/ui/lib/portalSettings.tsx';
import { companyInfoLabel } from '@l2u/ui/lib/companyInfo.ts';
import {
  configuredFieldValues,
  filterWithStatusCounts,
  parseFieldFilterParam,
  parseStatusFilterParam,
  type StatusFilter,
} from '../lib/companyFilter.ts';
import { companyFilterOptions } from '../lib/companyFilterOptions.ts';
import { ActiveFilterChips } from '../components/ActiveFilterChips.tsx';
import { vendorFilterChips, type VendorFilterChipRemoval } from '../lib/vendorFilterChips.ts';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { canWrite } from '../lib/adminAccess.ts';
import { paginate } from '../lib/pagination.ts';
import type { AdminCompanyListRow, Company } from '@l2u/ui/lib/types.ts';
import { buildVendorCsv } from '../lib/vendorCsv.ts';
import { resendActionForStatus } from '../lib/resendAction.ts';
import { vendorListName } from '../lib/vendorListName.ts';
import {
  compareVendorsBy,
  DEFAULT_VENDOR_SORT,
  parseVendorSort,
  type VendorSort,
  type VendorSortDir,
  type VendorSortKey,
} from '../lib/vendorSort.ts';
import { clearStoredFilter, readStoredFilter, writeStoredFilter } from '../lib/filterStorage.ts';
import {
  clearVendorFilters,
  parseVendorsSearch,
  vendorFilterPersistAction,
  vendorFilterRestoreSearch,
  VENDORS_FILTERS_KEY,
} from '../lib/vendorFilterParams.ts';

const PAGE_SIZES = [15, 25, 50, 100, 200, 300] as const;
const DEFAULT_PAGE_SIZE = 15;
const PAGE_SIZE_STORAGE_KEY = 'vendors.pageSize';

// How long the search box settles before the list re-filters. Long enough to
// skip the intermediate states of a typed word, short enough to still feel
// immediate — below ~120ms it stops coalescing anything on a fast typist, and
// past ~250ms the delay becomes noticeable on a short list.
const SEARCH_DEBOUNCE_MS = 180;

// Remembered rows-per-page preference: an explicit ?size= in the URL wins (so a
// shared/bookmarked link is exact), else the last choice saved to localStorage,
// else the default (15).
function readStoredPageSize(): number {
  try {
    const n = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    return (PAGE_SIZES as readonly number[]).includes(n) ? n : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
}

export function AdminCompanies() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () => adminApi.get<{ companies: AdminCompanyListRow[] }>('/api/companies'),
  });

  const { settings } = usePortalSettings();
  const { user } = useAdminAuth();
  // Presentation only -- requireWriteRole on the API is the enforcement. This
  // exists so a viewer is not shown a control the server would refuse.
  const mayWrite = canWrite(user?.role);

  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [filter, setFilter] = useState(() => searchParams.get('q') ?? '');
  // Keep the search box in sync when ?q= changes in the URL without a remount
  // (e.g. the restore effect below brings stored params onto a bare /vendors).
  // Typing flows local→URL via setSearch; this covers the URL→local direction.
  useEffect(() => {
    setFilter(searchParams.get('q') ?? '');
  }, [searchParams]);

  // Debounced mirror of `filter`, and the value the list work actually reads.
  //
  // The split is the point: `filter` drives the input so typing never stalls,
  // while the ten-field search over every loaded vendor — plus the sort and the
  // page slice that depend on it — runs only once the admin stops typing. At a
  // few dozen vendors this is invisible; at the few thousand this list is now
  // built for it is the difference between smooth typing and dropped frames.
  //
  // Seeded from `filter` so a page entered with ?q= already set filters on its
  // first render rather than flashing the unfiltered list for one debounce.
  const [debouncedFilter, setDebouncedFilter] = useState(filter);
  useEffect(() => {
    if (debouncedFilter === filter) return;
    const timer = setTimeout(() => setDebouncedFilter(filter), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filter, debouncedFilter]);

  // Restore-only half of the persistence split (2026-08-06 design, "The
  // setters own storage; the effect only restores"). Fires on every params
  // change but acts only when the URL carries no filter key AND storage holds
  // one — which, because every setter below clears storage as it empties the
  // params, is reachable only by a navigation that ran no setter: the sidebar
  // "Vendors" link (a bare /vendors that does NOT remount this page), a bare
  // bookmark, or the detail page's "← Vendors" link with an empty
  // vendorsListSearch. Restore is a SINGLE setSearchParams call (chained
  // functional updates in one tick clobber each other — see the clearFilters
  // comment below), and converges: the restored URL carries a filter key, so
  // the second pass restores nothing.
  useEffect(() => {
    const restore = vendorFilterRestoreSearch(
      searchParams,
      readStoredFilter(VENDORS_FILTERS_KEY, parseVendorsSearch),
    );
    if (restore !== null) setSearchParams(new URLSearchParams(restore), { replace: true });
  }, [searchParams, setSearchParams]);

  // Write-through half: the single funnel through which EVERY list-state
  // param change flows. Apply the mutation, then mirror the result to storage
  // — or CLEAR storage when no filter key remains, so removing the last
  // filter (emptying the search box, picking the "All" pill, deselecting the
  // last dropdown value, returning to page 1 or the default size) cannot be
  // snapped back by the restore effect above. react-router resolves the
  // functional updater synchronously, exactly once per call, so the storage
  // side effect cannot double-fire. Route every new setter through this — one
  // that calls setSearchParams directly leaves storage stale.
  const updateListParams = (mutate: (p: URLSearchParams) => void) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        mutate(p);
        const action = vendorFilterPersistAction(p);
        if (action.kind === 'write') {
          writeStoredFilter(VENDORS_FILTERS_KEY, { search: action.search });
        } else {
          clearStoredFilter(VENDORS_FILTERS_KEY);
        }
        return p;
      },
      { replace: true },
    );
  };

  // Page number + search text live in the URL (alongside the status/field
  // filters) so the browser Back button — and the detail page's "← Vendors"
  // link — restore the exact page and filters the admin left off on.
  const page = Math.max(1, Math.floor(Number(searchParams.get('page')) || 1));
  const setPage = (next: number) => {
    updateListParams((p) => {
      if (next <= 1) p.delete('page');
      else p.set('page', String(next));
    });
  };

  // Rows per page: URL ?size= wins, else the localStorage preference, else 15.
  const sizeParam = Number(searchParams.get('size'));
  const pageSize = (PAGE_SIZES as readonly number[]).includes(sizeParam)
    ? sizeParam
    : readStoredPageSize();
  const setPageSize = (next: number) => {
    try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(next));
    } catch {
      /* ignore — private mode / storage disabled */
    }
    updateListParams((p) => {
      if (next === DEFAULT_PAGE_SIZE) p.delete('size');
      else p.set('size', String(next));
      p.delete('page'); // a size change resets to the first page
    });
  };

  // Keep the search box in local state (snappy typing, no cursor jumps) but
  // mirror it to ?q= so it survives navigation; a new search resets to page 1.
  const setSearch = (next: string) => {
    setFilter(next);
    updateListParams((p) => {
      if (next.trim() === '') p.delete('q');
      else p.set('q', next);
      p.delete('page');
    });
  };

  // Sort lives in the URL alongside the filters, so Back and the detail page's
  // "← Vendors" link restore the column the admin was looking at. Validated on
  // read (the URL and its storage mirror are both user-writable), and a change
  // resets to page 1 like every filter setter — page 3 of an old ordering holds
  // different rows under a new one.
  const sort = parseVendorSort(searchParams.get('sort'), searchParams.get('dir'));
  const setSort = (key: VendorSortKey) => {
    // Same column toggles direction; a new column starts ascending.
    const dir: VendorSortDir = sort.key === key && sort.dir === 'asc' ? 'desc' : 'asc';
    updateListParams((p) => {
      // The default sort is the absence of params, so it never litters the URL.
      if (key === DEFAULT_VENDOR_SORT.key && dir === DEFAULT_VENDOR_SORT.dir) {
        p.delete('sort');
        p.delete('dir');
      } else {
        p.set('sort', key);
        p.set('dir', dir);
      }
      p.delete('page');
    });
  };

  const statusFilter = parseStatusFilterParam(searchParams.get('status'));
  const setStatusFilter = (next: StatusFilter) => {
    updateListParams((p) => {
      if (next === 'all') p.delete('status');
      else p.set('status', next);
      p.delete('page'); // a filter change resets to the first page
    });
  };

  // All three dropdowns list what Settings curates (contractor type and station
  // narrowed to the signed-in user's scope; cluster region has no assignment
  // axis, so it is never narrowed). companyFilterOptions carries the reasoning
  // and keeps this page, the Dashboard and Expiring Documents in agreement.
  // hasNotSet still comes from the rows — it reports whether "(Not set)" would
  // match anything.
  const optionPool = useMemo(() => companyFilterOptions(settings, user), [settings, user]);
  const typeValues = useMemo(
    () =>
      configuredFieldValues(optionPool.contractorTypes, data?.companies ?? [], 'contractor_types'),
    [optionPool, data],
  );
  const regionValues = useMemo(
    () =>
      configuredFieldValues(optionPool.clusterRegions, data?.companies ?? [], 'cluster_regions'),
    [optionPool, data],
  );
  const stationValues = useMemo(
    () => configuredFieldValues(optionPool.stations, data?.companies ?? [], 'stations'),
    [optionPool, data],
  );

  // Field filters are multi-select: each URL key is repeatable
  // (?type=a&type=b&region=x&station=y). Read every value with getAll and
  // validate against the current option pool.
  const typeFilter = parseFieldFilterParam(searchParams.getAll('type'), typeValues);
  const regionFilter = parseFieldFilterParam(searchParams.getAll('region'), regionValues);
  const stationFilter = parseFieldFilterParam(searchParams.getAll('station'), stationValues);

  // Rewrite a repeatable field-filter param: drop every existing entry for the
  // key, then append one per selected value (empty selection = no key).
  const setFieldFilter = (key: 'type' | 'region' | 'station', next: string[]) => {
    updateListParams((p) => {
      p.delete(key);
      for (const v of next) p.append(key, v);
      p.delete('page'); // a filter change resets to the first page
    });
  };

  // One pass yields both the filtered rows and the faceted status counts.
  //
  // Status pill counts are faceted: they reflect every active filter EXCEPT
  // status itself (a facet never filters its own counts — otherwise clicking a
  // pill would zero all the others), and they include deactivated rows so the
  // Deactivated pill shows a real count. That makes the count set the SUPERSET
  // of the list set, so filterWithStatusCounts computes the expensive
  // conditions — the ten-field search and three set filters — once and derives
  // both, instead of the two full filterCompanies calls this replaced.
  //
  // `debouncedFilter`, not `filter`: the input stays instant via local state
  // while the list work runs on the settled value.
  const { rows: filteredRows, counts: statusCounts } = useMemo(
    () =>
      filterWithStatusCounts(data?.companies ?? [], {
        query: debouncedFilter,
        status: statusFilter,
        contractorType: typeFilter,
        clusterRegion: regionFilter,
        station: stationFilter,
      }),
    [data, debouncedFilter, statusFilter, typeFilter, regionFilter, stationFilter],
  );

  // Sorting stays separate from filtering: it orders by the RENDERED cell text
  // (vendorListName's fallback, joined lists, the status badge label), which is
  // a presentation concern companyFilter.ts knows nothing about. It must still
  // sit between the filter and the page slice, or page 2 would hold rows that
  // belong on page 1. filterWithStatusCounts returns a fresh array, so sorting
  // it in place is safe.
  // Defaults to A→Z on the Vendor column (registry name, else legal, else
  // Trading Name) — a fallback the server's `updated_at desc` cannot express.
  // The CSV export reads this same memo, so the download matches the screen.
  const companies = useMemo(
    () => [...filteredRows].sort(compareVendorsBy(sort.key, sort.dir)),
    [filteredRows, sort],
  );

  const { counts, total: facetedTotal } = statusCounts;
  // Deactivated companies are parked — the page's headline total reflects only
  // active companies (the Deactivated pill carries its own count).
  const grandTotal = useMemo(
    () => (data?.companies ?? []).filter((c) => c.status !== 'deactivated').length,
    [data],
  );

  // One derivation, not two. The chip row and "is this list filtered?" are the
  // same question, so filtersActive reads off the chips rather than repeating
  // their conditions — which makes a filter that narrows the table while
  // showing no chip structurally impossible. That combination is what made a
  // stale stored filter read as a data bug: the header said "10 of 22", no
  // control looked set, and one page of results hid the pager too.
  //
  // `filter`, not `debouncedFilter`: this mirrors the expression it replaces,
  // so the chip appears as the admin types while the table settles behind it.
  const filterChips = useMemo(
    () =>
      vendorFilterChips({
        query: filter,
        status: statusFilter,
        type: typeFilter,
        region: regionFilter,
        station: stationFilter,
        // Portal-configurable labels, so a renamed field renames its chip.
        labels: {
          type: companyInfoLabel(settings, 'contractorType'),
          region: companyInfoLabel(settings, 'clusterRegion'),
          station: companyInfoLabel(settings, 'station'),
        },
      }),
    [filter, statusFilter, typeFilter, regionFilter, stationFilter, settings],
  );
  const filtersActive = filterChips.length > 0;

  // Maps a chip's removal instruction onto the setters that already own each
  // param, so the chip row never touches the URL or storage itself.
  const removeChip = (removal: VendorFilterChipRemoval) => {
    if (removal.kind === 'query') {
      setSearch('');
      return;
    }
    if (removal.kind === 'status') {
      setStatusFilter('all');
      return;
    }
    const current =
      removal.key === 'type' ? typeFilter : removal.key === 'region' ? regionFilter : stationFilter;
    // Drops only this value; the field's other selections survive.
    setFieldFilter(
      removal.key,
      current.filter((v) => v !== removal.value),
    );
  };

  // Each filter setter already resets to page 1 (it drops the ?page= param), so
  // no post-render page-reset effect is needed.
  // One updateListParams call clears all the filter params: react-router's
  // functional updater reads the *location's* current params, so chained
  // calls in the same tick would clobber each other. clearVendorFilters pins
  // the storage-then-URL order; with the funnel's write-through the explicit
  // clear is belt-and-braces — but note ?size= is deliberately NOT deleted
  // (page size is a preference, not a filter; filtersActive excludes it), so
  // when a non-default size is active the funnel re-mirrors 'size=NN' and the
  // sidebar link keeps the admin's chosen page size.
  const clearFilters = () => {
    clearVendorFilters(
      () => clearStoredFilter(VENDORS_FILTERS_KEY),
      () => {
        setFilter('');
        updateListParams((p) => {
          p.delete('status');
          p.delete('type');
          p.delete('region');
          p.delete('station');
          p.delete('q');
          p.delete('page');
        });
      },
    );
  };

  const [sentModal, setSentModal] = useState<{
    company: AdminCompanyListRow;
    kind: 'invite' | 'login_link';
  } | null>(null);
  const inviteMutation = useMutation({
    mutationFn: (id: string) =>
      adminApi.post<{ sent: boolean; expires_at: string }>(`/api/companies/${id}/invite`),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['companies'] });
      const company = data?.companies.find((c) => c.id === id) ?? null;
      setSentModal(company ? { company, kind: 'invite' } : null);
    },
  });

  const resendLoginMutation = useMutation({
    mutationFn: (id: string) =>
      adminApi.post<{ sent: boolean }>(`/api/companies/${id}/resend-login-link`),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['companies'] });
      const company = data?.companies.find((c) => c.id === id) ?? null;
      setSentModal(company ? { company, kind: 'login_link' } : null);
    },
  });

  const [confirmAction, setConfirmAction] = useState<{
    company: AdminCompanyListRow;
    action: 'deactivate' | 'reactivate';
  } | null>(null);
  // Task 11: resending an invite or login link emails the vendor, so confirm
  // first — a mis-click otherwise sends real mail with no undo.
  const [resendConfirm, setResendConfirm] = useState<{
    company: AdminCompanyListRow;
    kind: 'invite' | 'login';
  } | null>(null);

  const deactivateMutation = useMutation({
    mutationFn: (id: string) =>
      adminApi.post<{ company: Company }>(`/api/companies/${id}/deactivate`),
    onSuccess: () => {
      setConfirmAction(null);
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });
  const reactivateMutation = useMutation({
    mutationFn: (id: string) =>
      adminApi.post<{ company: Company }>(`/api/companies/${id}/reactivate`),
    onSuccess: () => {
      setConfirmAction(null);
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });
  // The modal's confirm button drives whichever mutation matches the pending action.
  const confirmMutation =
    confirmAction?.action === 'reactivate' ? reactivateMutation : deactivateMutation;

  // Export the currently-filtered vendors (all pages, not just the visible
  // page) to a CSV the browser downloads.
  //
  // This now takes an API round-trip, where it used to build the file straight
  // from the loaded list. The CSV carries 38 columns (VENDOR_CSV_COLUMNS) and
  // the list payload deliberately carries ~20, so the wide rows have to be
  // fetched — POST /api/companies/export hydrates the ids that currently pass
  // the filters. That trade is the whole point of the narrowing: one slower
  // click in exchange for a page load that never ships the address block and
  // the abn_* quartet for every vendor to every admin.
  //
  // The ids come from `companies`, the sorted+filtered memo, so the export
  // still matches exactly what the screen shows. Order is re-applied here
  // because the server returns its own (`updated_at desc`).
  const exportMutation = useMutation({
    mutationFn: async () => {
      const { companies: wide } = await adminApi.post<{ companies: Company[] }>(
        '/api/companies/export',
        { ids: companies.map((c) => c.id) },
      );
      const order = new Map(companies.map((c, i) => [c.id, i]));
      return [...wide].sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    },
    onSuccess: (rows) => {
      const csv = buildVendorCsv(rows);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendors-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });

  // Hoisted above the early return: useMfaStatuses is a hook and must run on
  // every render, and it needs the page's ids. paginate() is pure and returns
  // an empty page while the list is still loading, so the query stays disabled
  // until there is something to ask about.
  const {
    pageItems,
    page: currentPage,
    totalPages,
    from,
    to,
    total,
  } = paginate(companies, page, pageSize);

  // ONE request for the whole visible page (spec D9) — a per-row lookup here
  // would be 15+ round trips and undo the payload work in #86/#88.
  const mfaStatuses = useMfaStatuses(pageItems.map((c) => c.id));

  if (isLoading) return <CenteredSpinner />;

  // Stash the current list URL (page + filters) so the detail page's "← Vendors"
  // link can return the admin to exactly this view.
  const listSearch = searchParams.toString();

  return (
    <div className="space-y-5">
      {/* Title row: heading + subtitle on the left, primary action top-right. */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Vendors</h1>
          <p className="mt-1 text-sm text-slate-500" aria-live="polite">
            {filtersActive
              ? `${companies.length} of ${grandTotal} ${grandTotal === 1 ? 'vendor' : 'vendors'}`
              : `${grandTotal} total ${grandTotal === 1 ? 'vendor' : 'vendors'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Btn
            variant="info"
            onClick={() => exportMutation.mutate()}
            loading={exportMutation.isPending}
            disabled={companies.length === 0}
          >
            Export CSV
          </Btn>
          {mayWrite && <Btn onClick={() => setCreateOpen(true)}>+ New vendor</Btn>}
        </div>
      </div>

      {/* Status filter pills — replaces the old dropdown. */}
      <StatusFilterTabs
        value={statusFilter}
        counts={counts}
        total={facetedTotal}
        onChange={setStatusFilter}
      />

      {/* Companies card: the search row sits at the top of the same card as the
          table (per the screenshot), so it stays visible even when no rows match
          and the user needs to clear the search. */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                <NavIcon name="search" size={18} />
              </span>
              <TextInput
                className="pl-10 pr-9"
                placeholder="Search name, legal name, contact, vendor #, contract # or territory…"
                aria-label="Search vendors"
                value={filter}
                onChange={(e) => setSearch(e.target.value)}
              />
              {filter && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-600"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <SearchableMultiSelect
              label={companyInfoLabel(settings, 'contractorType')}
              unitLabel="contractor types"
              values={typeValues.values}
              selected={typeFilter}
              includeNotSet
              onChange={(next) => setFieldFilter('type', next)}
            />
            <SearchableMultiSelect
              label={companyInfoLabel(settings, 'clusterRegion')}
              unitLabel="cluster regions"
              values={regionValues.values}
              selected={regionFilter}
              includeNotSet
              onChange={(next) => setFieldFilter('region', next)}
            />
            <SearchableMultiSelect
              label={companyInfoLabel(settings, 'station')}
              unitLabel="stations"
              values={stationValues.values}
              selected={stationFilter}
              includeNotSet
              onChange={(next) => setFieldFilter('station', next)}
            />
          </div>

          {/* Second row of the same card header, so an active filter is
              visible above BOTH the table and the empty state. */}
          <ActiveFilterChips chips={filterChips} onRemove={removeChip} onClearAll={clearFilters} />
        </div>

        {companies.length === 0 ? (
          // Borderless empty block (mirrors EmptyState's content) so it sits
          // inside the card rather than drawing its own dashed border.
          <div className="p-12 text-center">
            <h3 className="text-base font-semibold text-slate-900">
              {filtersActive ? 'No matching vendors' : 'No vendors yet'}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {filtersActive
                ? 'No vendors match your current filters.'
                : 'Add a vendor to invite them to complete the questionnaire.'}
            </p>
            <div className="mt-4 flex justify-center">
              {filtersActive ? (
                <Btn variant="secondary" onClick={clearFilters}>
                  Clear filters
                </Btn>
              ) : mayWrite ? (
                <Btn onClick={() => setCreateOpen(true)}>+ New vendor</Btn>
              ) : null}
            </div>
          </div>
        ) : (
          // The columns overflow narrow viewports and the card clips
          // (overflow-hidden), so let the table scroll sideways instead of
          // crushing the cells.
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <tr>
                  <SortableTh label="Vendor" sortKey="vendor" sort={sort} onSort={setSort} />
                  <SortableTh label="Trading Name" sortKey="trading" sort={sort} onSort={setSort} />
                  {/* Tenant-renamable labels (Settings), same source as the
                      filter dropdowns above. */}
                  <SortableTh
                    label={companyInfoLabel(settings, 'contractorType')}
                    sortKey="type"
                    sort={sort}
                    onSort={setSort}
                  />
                  <SortableTh
                    label={companyInfoLabel(settings, 'clusterRegion')}
                    sortKey="region"
                    sort={sort}
                    onSort={setSort}
                  />
                  <SortableTh
                    label={companyInfoLabel(settings, 'station')}
                    sortKey="station"
                    sort={sort}
                    onSort={setSort}
                  />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={setSort} />
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageItems.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="relative inline-flex shrink-0">
                          <Avatar name={c.company_name} />
                          <MfaBadge size="sm" enabled={mfaStatuses.get(c.id)} />
                        </span>
                        <Link
                          to={`/vendors/${c.id}`}
                          state={{ vendorsListSearch: listSearch ? `?${listSearch}` : '' }}
                          className="font-medium text-slate-900 hover:underline"
                        >
                          {/* Registry-first, matching the detail page's Legal entity
                              name row: ABR entity name → vendor-entered legal name →
                              admin-entered name. A blank Vendor cell would be worse
                              than a temporary duplicate of Trading Name. */}
                          {vendorListName(c.abn_entity_name, c.legal_entity_name, c.company_name)}
                        </Link>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {/* The name the admin entered at invite IS the trading name
                          (0070 dropped the separate column on that reasoning). */}
                      {c.company_name}
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {c.contractor_types && c.contractor_types.length > 0 ? (
                        c.contractor_types.join(', ')
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {c.cluster_regions && c.cluster_regions.length > 0 ? (
                        c.cluster_regions.join(', ')
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {c.stations && c.stations.length > 0 ? (
                        c.stations.join(', ')
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <CompanyStatusBadge status={c.status} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {mayWrite &&
                          (c.status === 'deactivated' ? (
                            <IconActionBtn
                              label="Reactivate vendor"
                              icon="rotate-ccw"
                              onClick={() => setConfirmAction({ company: c, action: 'reactivate' })}
                            />
                          ) : (
                            <>
                              {resendActionForStatus(c.status, c.invited_at ?? null) ===
                              'invite' ? (
                                <IconActionBtn
                                  label={c.invited_at ? 'Resend invite' : 'Send invite'}
                                  icon="mail"
                                  loading={
                                    inviteMutation.isPending && inviteMutation.variables === c.id
                                  }
                                  onClick={() => setResendConfirm({ company: c, kind: 'invite' })}
                                />
                              ) : (
                                <IconActionBtn
                                  label="Resend login link"
                                  icon="mail"
                                  loading={
                                    resendLoginMutation.isPending &&
                                    resendLoginMutation.variables === c.id
                                  }
                                  onClick={() => setResendConfirm({ company: c, kind: 'login' })}
                                />
                              )}
                              <IconActionBtn
                                label="Deactivate vendor"
                                icon="ban"
                                onClick={() =>
                                  setConfirmAction({ company: c, action: 'deactivate' })
                                }
                              />
                            </>
                          ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > 0 && (
          <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 text-slate-500">
              <span>
                Showing {from}–{to} of {total}
              </span>
              <label className="flex items-center gap-1.5">
                <span>Per page</span>
                <Select
                  className="!w-auto"
                  aria-label="Rows per page"
                  value={String(pageSize)}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </label>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Btn
                  size="sm"
                  variant="secondary"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </Btn>
                <span className="text-slate-500">
                  Page {currentPage} of {totalPages}
                </span>
                <Btn
                  size="sm"
                  variant="secondary"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </Btn>
              </div>
            )}
          </div>
        )}
      </div>

      {inviteMutation.error && <ErrorMessage error={inviteMutation.error} />}
      {resendLoginMutation.error && <ErrorMessage error={resendLoginMutation.error} />}

      <Modal
        open={sentModal !== null}
        onClose={() => setSentModal(null)}
        title={sentModal?.kind === 'login_link' ? 'Login link sent' : 'Invitation sent'}
        size="sm"
        footer={
          <Btn variant="info" onClick={() => setSentModal(null)}>
            Done
          </Btn>
        }
      >
        <p className="text-sm text-slate-600">
          {sentModal?.kind === 'login_link'
            ? `A login link email has been sent to ${sentModal.company.email ?? 'the vendor contact'}.`
            : `An invitation email has been sent to ${sentModal?.company.email ?? 'the vendor contact'}.`}
        </p>
      </Modal>

      {confirmAction && (
        <ConfirmModal
          open
          title={confirmAction.action === 'deactivate' ? 'Deactivate vendor' : 'Reactivate vendor'}
          confirmLabel={confirmAction.action === 'deactivate' ? 'Deactivate' : 'Reactivate'}
          danger={confirmAction.action === 'deactivate'}
          loading={confirmMutation.isPending}
          onConfirm={() => confirmMutation.mutate(confirmAction.company.id)}
          onClose={() => {
            setConfirmAction(null);
            deactivateMutation.reset();
            reactivateMutation.reset();
          }}
        >
          {confirmAction.action === 'deactivate' ? (
            <>
              Are you sure you want to deactivate{' '}
              <span className="font-semibold">{confirmAction.company.company_name}</span>?
              They&apos;ll be removed from the vendors list and lose portal access. Their records
              are kept — you can reactivate them anytime.
            </>
          ) : (
            <>
              Are you sure you want to reactivate{' '}
              <span className="font-semibold">{confirmAction.company.company_name}</span>?
              They&apos;ll return to their previous status and regain portal access.
            </>
          )}
          {confirmMutation.error != null && (
            <div className="mt-3">
              <ErrorMessage error={confirmMutation.error} />
            </div>
          )}
        </ConfirmModal>
      )}

      {/* Task 11: confirm before sending invite / login-link email. */}
      {resendConfirm && (
        <ConfirmModal
          open
          title={
            resendConfirm.kind === 'invite'
              ? resendConfirm.company.invited_at
                ? 'Resend invite'
                : 'Send invite'
              : 'Resend login link'
          }
          confirmLabel="Send"
          loading={inviteMutation.isPending || resendLoginMutation.isPending}
          onConfirm={() => {
            const { company, kind } = resendConfirm;
            (kind === 'invite' ? inviteMutation : resendLoginMutation).mutate(company.id, {
              onSettled: () => setResendConfirm(null),
            });
          }}
          onClose={() => setResendConfirm(null)}
        >
          Send a {resendConfirm.kind === 'invite' ? 'new invitation' : 'login link'} email to{' '}
          <span className="font-semibold">{resendConfirm.company.email}</span>?
        </ConfirmModal>
      )}

      {/* Mount-gated, not `open`-gated: the modal owns its draft form state, so
          it has to unmount on close for a reopen to start blank. Rendering it
          unconditionally kept a cancelled draft (and any error) alive. */}
      {createOpen && <CreateCompanyModal onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

/**
 * A sortable column header. The whole header is the control, so the click
 * target matches what it looks like.
 *
 * aria-sort on the <th> is what screen readers announce, so it carries the real
 * state; the caret is decorative and hidden from them. The inactive caret stays
 * rendered at low opacity rather than being swapped in on hover — it advertises
 * that the column is sortable, and reserving the space stops every header
 * shifting sideways as the active column changes.
 */
export function SortableTh({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: VendorSortKey;
  sort: VendorSort;
  onSort: (key: VendorSortKey) => void;
}) {
  const active = sort.key === sortKey;
  const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th className="px-6 py-3" aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        // Inherits the thead's uppercase/tracking so it reads as a header, not
        // a button dropped into one.
        className="group inline-flex items-center gap-1 font-medium uppercase tracking-wide text-slate-500 hover:text-slate-900 focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-red-500"
        title={`Sort by ${label}`}
      >
        {label}
        <span
          aria-hidden="true"
          className={`text-[0.65rem] leading-none ${
            active ? 'text-slate-900' : 'text-slate-400 opacity-0 group-hover:opacity-100'
          }`}
        >
          {active && sort.dir === 'desc' ? '▼' : '▲'}
        </span>
      </button>
    </th>
  );
}

// Compact icon-only action for table rows. Btn's sm padding is sized for text,
// so square it up with an important px override; the label reaches assistive
// tech via aria-label and sighted users via the title tooltip.
function IconActionBtn({
  label,
  icon,
  onClick,
  loading = false,
}: {
  label: string;
  icon: IconName;
  onClick: () => void;
  loading?: boolean;
}) {
  return (
    <Btn
      size="sm"
      variant="ghost"
      className="!px-2"
      aria-label={label}
      title={label}
      loading={loading}
      onClick={onClick}
    >
      {loading ? null : <NavIcon name={icon} size={16} />}
    </Btn>
  );
}

type CreateForm = {
  company_name: string;
  contact_name: string;
  email: string;
  contractor_types: string[];
  vendor_number: string;
  territory: string;
  contract_numbers: string[];
  cluster_regions: string[];
  stations: string[];
};

const EMPTY_CREATE_FORM: CreateForm = {
  company_name: '',
  contact_name: '',
  email: '',
  contractor_types: [],
  vendor_number: '',
  territory: '',
  contract_numbers: [],
  cluster_regions: [],
  stations: [],
};

// Mounted only while open (see the call site) — matching CommentModal /
// QuestionEditor — so every field below resets by unmounting.
function CreateCompanyModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { settings } = usePortalSettings();
  const { user } = useAdminAuth();
  const [form, setForm] = useState<CreateForm>(EMPTY_CREATE_FORM);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  // A restricted admin can only create vendors inside their own scope —
  // otherwise the vendor would vanish from their list the moment it saves.
  // Cluster region keeps the full configured list: it has no assignment axis,
  // and deriving it from existing vendors would make the first vendor in a
  // legitimately configured region impossible to create.
  const { contractorTypes, clusterRegions, stations } = companyFilterOptions(settings, user);

  function field<K extends keyof CreateForm>(key: K, value: CreateForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        company_name: form.company_name,
        contact_name: form.contact_name,
        email: form.email,
      };
      payload.cluster_regions = form.cluster_regions;
      payload.stations = form.stations;
      const vendorNumberTrimmed = form.vendor_number.trim();
      if (vendorNumberTrimmed.length > 0) payload.vendor_number = vendorNumberTrimmed;
      const territoryTrimmed = form.territory.trim();
      if (territoryTrimmed.length > 0) payload.territory = territoryTrimmed;
      payload.contractor_types = form.contractor_types;
      if (form.contract_numbers.length > 0) payload.contract_numbers = form.contract_numbers;
      const { company } = await adminApi.post<{ company: Company }>('/api/companies', payload);
      // Task 12: the invite always goes out — the opt-in checkbox is gone.
      await adminApi.post(`/api/companies/${company.id}/invite`).catch((e) => {
        throw e instanceof ApiError ? e : new Error('Created, but invite failed');
      });
      await qc.invalidateQueries({ queryKey: ['companies'] });
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="New vendor"
      size="lg"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            onClick={submit}
            loading={submitting}
            disabled={
              !form.company_name ||
              !form.email ||
              form.contractor_types.length === 0 ||
              form.cluster_regions.length === 0 ||
              form.stations.length === 0
            }
          >
            Create &amp; invite
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Vendor legal name" required>
            <TextInput
              value={form.company_name}
              onChange={(e) => field('company_name', e.target.value)}
            />
          </Field>
          <Field label="Contact name" required>
            <TextInput
              value={form.contact_name}
              onChange={(e) => field('contact_name', e.target.value)}
            />
          </Field>
          <Field label="Contact email" required>
            <TextInput
              type="email"
              value={form.email}
              onChange={(e) => field('email', e.target.value)}
            />
          </Field>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 text-sm font-semibold text-slate-700">
            Pre-filled vendor information
          </div>
          <p className="mb-3 text-xs text-slate-500">
            These values appear pre-filled on the vendor&apos;s questionnaire when they accept the
            invite. Contractor type, cluster region and station are required (at least one of each).
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label={companyInfoLabel(settings, 'contractorType')}
              required
              hint={
                contractorTypes.length === 0
                  ? 'Add contractor types in Settings to populate these options.'
                  : undefined
              }
            >
              <MultiSelect
                label={companyInfoLabel(settings, 'contractorType')}
                fullWidth
                values={contractorTypes}
                selected={form.contractor_types}
                onChange={(next) => field('contractor_types', next)}
              />
            </Field>
            <Field label={companyInfoLabel(settings, 'vendorNumber')}>
              <TextInput
                value={form.vendor_number}
                onChange={(e) => field('vendor_number', e.target.value)}
              />
            </Field>
            <Field label={companyInfoLabel(settings, 'territory')}>
              <TextInput
                value={form.territory}
                onChange={(e) => field('territory', e.target.value)}
              />
            </Field>
            <Field label={companyInfoLabel(settings, 'contractNumber')}>
              <StringListEditor
                value={form.contract_numbers}
                onChange={(list) => field('contract_numbers', list)}
                itemLabel="contract number"
                addLabel="Add contract number"
                maxLength={255}
              />
            </Field>
            <Field
              label={companyInfoLabel(settings, 'clusterRegion')}
              required
              hint={
                clusterRegions.length === 0
                  ? 'Add cluster regions in Settings to populate this dropdown.'
                  : undefined
              }
            >
              <MultiSelect
                label={companyInfoLabel(settings, 'clusterRegion')}
                fullWidth
                values={clusterRegions}
                selected={form.cluster_regions}
                onChange={(next) => field('cluster_regions', next)}
              />
            </Field>
            <Field
              label={companyInfoLabel(settings, 'station')}
              required
              hint={
                stations.length === 0
                  ? 'Add stations in Settings to populate this dropdown.'
                  : undefined
              }
            >
              <MultiSelect
                label={companyInfoLabel(settings, 'station')}
                fullWidth
                values={stations}
                selected={form.stations}
                onChange={(next) => field('stations', next)}
              />
            </Field>
          </div>
        </div>

        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
